/**
 * Morse — die Fortschrittsbalken als reine Rechnung. (23.10.2026, Neuordnung der Seite)
 *
 * Martin: „mehr Ordnung, Redesign, und eine Progressbar … komplette Reformation dieses Moduls“. Drei Balken, alle aus dem
 * Stand, den die Seite schon hat (keine neue Server-Route):
 *   - `stufenLeiste`: wo man in der Koch-Reihe steht („Stufe 3 von 40“) — ein Strich je Stufe;
 *   - `zielAnteile`: wie weit jede der vier Bedingungen des Aufstiegs ist (die Zahlen rechnet `kochStufenZiel`). Zähl-
 *     Bedingungen zeigen den Weg zum Soll (100 % NUR, wenn erfüllt), Quoten die echte Trefferquote mit der Schwelle als
 *     Marke. Keine Gesamtprozentzahl: neben „Insgesamt 85 % richtig“ läse sie sich als zweite Quote (Prüfer 23.10.2026);
 *   - `rundenVerlauf`: die laufende Übung — Aufgabe für Aufgabe richtig/falsch, die jetzige, die offenen. Mit fester Länge
 *     (Tippen: zehn Gruppen) oder als gleitendes Fenster der letzten Aufgaben (Augen zu läuft ohne Ende).
 *
 * Hier steht nur die Rechnung (prüfbar ohne DOM, tests/frontend/morse-balken.test.ts). Gezeichnet wird in morse-ziel.ts
 * (Stufe, Ziel) und morse-teile.ts (Verlauf der Bühne) — ohne `style`-Attribut: Striche tragen `data-art`, Balken sind
 * `<progress>`-Elemente mit `value`.
 */

/** Ein Strich der Stufenleiste. */
export type StufenStrich = "erreicht" | "jetzt" | "offen";

export interface StufenLeiste {
  stufe: number;
  /** Die erste Stufe der Koch-Reihe (1) — `aria-valuemin` der Leiste. */
  erste: number;
  letzte: number;
  /** 0…1 — 0 auf der ersten Stufe, 1 auf der letzten. */
  anteil: number;
  /** Ganze Prozent, 0…100. */
  prozent: number;
  striche: StufenStrich[];
  /** Für Vorleser: „Stufe 3 von 40“. */
  text: string;
}

const endlich = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);
const klemme = (x: number, min: number, max: number): number => Math.min(max, Math.max(min, x));

/** Die erste Stufe der Koch-Reihe (shared/morse-koch.ts: Stufe 1 = K und M). */
export const ERSTE_STUFE = 1;

/** Die Leiste der Stufen — `null` ohne brauchbare Zahlen (dann zeichnet die Seite keine Leiste). */
export function stufenLeiste(stufe: unknown, letzte: unknown): StufenLeiste | null {
  if (!endlich(stufe) || !endlich(letzte) || letzte < ERSTE_STUFE) return null;
  const max = Math.floor(letzte);
  const s = klemme(Math.floor(stufe), ERSTE_STUFE, max);
  const striche: StufenStrich[] = [];
  for (let i = ERSTE_STUFE; i <= max; i++) striche.push(i < s ? "erreicht" : i === s ? "jetzt" : "offen");
  const anteil = max === ERSTE_STUFE ? 1 : (s - ERSTE_STUFE) / (max - ERSTE_STUFE);
  return { stufe: s, erste: ERSTE_STUFE, letzte: max, anteil, prozent: Math.floor(anteil * 100), striche, text: `Stufe ${s} von ${max}` };
}

/* ── Das Ziel der Stufe ─────────────────────────────────────────────── */

export type ZielArt = "zeichen" | "neu-anzahl" | "quote" | "neu-quote";

/** Was `zielAnteile` vom Ziel braucht — die Felder von `StufenZiel` (shared/morse-koch.ts). */
export interface ZielZahlen {
  ok: Readonly<Record<ZielArt, boolean>>;
  zeichen: number;
  mindestZeichen: number;
  quote: number | null;
  neuGesamt: number;
  mindestNeu: number;
  neuQuote: number | null;
  schwelle: number;
}

/**
 * Eine Bedingung als Balken. Zwei Arten (Prüfer-Auflage 23.10.2026):
 *   - „zahl“ (genug gehört, oft genug): der Balken ist der Weg zum Soll, dazu eine Prozentzahl (`prozent`);
 *   - „quote“ (richtig): der Balken ist die ECHTE Trefferquote, die Schwelle steht als Marke (`marke`) — eine Prozentzahl
 *     wie „94 %“ (= 85 % von 90 %) läse jeder als Trefferquote, darum gibt es hier keine (`prozent` ist `null`).
 */
export interface ZielAnteil {
  art: ZielArt;
  ok: boolean;
  form: "zahl" | "quote";
  /** Wert des Balkens, ganze Prozent 0…100. Bei „zahl“ höchstens 99, solange die Bedingung nicht erfüllt ist. */
  balken: number;
  /** Nur bei „zahl“: die Prozentzahl daneben (gleich `balken`). */
  prozent: number | null;
  /** Nur bei „quote“: die Schwelle in ganzen Prozent (die Marke auf dem Balken). */
  marke: number | null;
}

export interface ZielAnteile {
  punkte: ZielAnteil[];
  /** So viele der vier sind erfüllt (die Seite zeigt „2/4 erfüllt“ — bewusst keine Gesamtprozentzahl). */
  erfuellt: number;
}

/** Höchstens so viel zeigt ein Zähl-Balken, dessen Bedingung NICHT erfüllt ist. */
const FAST = 99;

function wegZumSoll(ist: number | null, soll: number, ok: boolean): number {
  if (ok) return 100;
  if (!endlich(ist) || !endlich(soll) || soll <= 0) return 0;
  return klemme(Math.floor((ist / soll) * 100), 0, FAST);
}

const quoteProzent = (q: number | null): number => (endlich(q) ? klemme(Math.floor(q * 100), 0, 100) : 0);

/** Je Bedingung, wie weit sie ist — `null` ohne Ziel (älterer Server, letzte Stufe). */
export function zielAnteile(z: ZielZahlen | null | undefined): ZielAnteile | null {
  if (!z || !z.ok) return null;
  const ok = (art: ZielArt): boolean => z.ok[art] === true;
  const marke = endlich(z.schwelle) ? klemme(Math.round(z.schwelle * 100), 0, 100) : null;
  const zahl = (art: ZielArt, ist: number, soll: number): ZielAnteil => {
    const b = wegZumSoll(ist, soll, ok(art));
    return { art, ok: ok(art), form: "zahl", balken: b, prozent: b, marke: null };
  };
  const quote = (art: ZielArt, q: number | null): ZielAnteil => ({ art, ok: ok(art), form: "quote", balken: quoteProzent(q), prozent: null, marke });
  const punkte = [zahl("zeichen", z.zeichen, z.mindestZeichen), zahl("neu-anzahl", z.neuGesamt, z.mindestNeu), quote("quote", z.quote), quote("neu-quote", z.neuQuote)];
  return { punkte, erfuellt: punkte.filter((p) => p.ok).length };
}

/* ── Der Verlauf der laufenden Übung ────────────────────────────────── */

export type VerlaufStrich = "richtig" | "falsch" | "jetzt" | "offen";

export interface RundenVerlauf {
  striche: VerlaufStrich[];
  richtig: number;
  falsch: number;
  /** Beantwortete Aufgaben (der ganzen Runde, nicht nur des Fensters). */
  erledigt: number;
  /** Feste Länge der Runde — `null`: offen (Augen zu). */
  gesamt: number | null;
  /** 0…1 der festen Runde; ohne feste Länge der Anteil richtig. */
  anteil: number;
  /** Für Vorleser, ein Satz. */
  text: string;
}

/**
 * Der Verlauf einer Runde. `ergebnisse[i]`: Aufgabe i richtig (`true`), falsch (`false`), noch ohne Antwort (`undefined`/`null`).
 * `gesamt`: feste Länge (Tippen) — sonst ein gleitendes Fenster der letzten `fenster` Aufgaben. `jetzt`: die Aufgabe, die
 * gerade dran ist (wird als „jetzt“ markiert, solange sie ohne Antwort ist).
 */
export function rundenVerlauf(
  ergebnisse: ReadonlyArray<boolean | null | undefined>,
  o: { gesamt?: number | null; jetzt?: number | null; fenster?: number } = {},
): RundenVerlauf {
  const liste = Array.isArray(ergebnisse) ? ergebnisse : [];
  const richtig = liste.filter((x) => x === true).length;
  const falsch = liste.filter((x) => x === false).length;
  const erledigt = richtig + falsch;
  const gesamt = endlich(o.gesamt) && o.gesamt > 0 ? Math.floor(o.gesamt) : null;
  const jetzt = endlich(o.jetzt) && o.jetzt >= 0 ? Math.floor(o.jetzt) : null;
  const strich = (i: number): VerlaufStrich => {
    const x = liste[i];
    if (x === true) return "richtig";
    if (x === false) return "falsch";
    return i === jetzt ? "jetzt" : "offen";
  };
  let striche: VerlaufStrich[];
  if (gesamt !== null) {
    striche = Array.from({ length: gesamt }, (_, i) => strich(i));
  } else {
    const fenster = endlich(o.fenster) && o.fenster > 0 ? Math.floor(o.fenster) : 20;
    const ende = Math.max(liste.length, jetzt === null ? 0 : jetzt + 1);
    const von = Math.max(0, ende - fenster);
    striche = Array.from({ length: ende - von }, (_, i) => strich(von + i));
  }
  const anteil = gesamt !== null ? Math.min(1, erledigt / gesamt) : erledigt > 0 ? richtig / erledigt : 0;
  const text =
    gesamt !== null
      ? `${erledigt} von ${gesamt} beantwortet, ${richtig} richtig, ${falsch} falsch.`
      : erledigt === 0
        ? "Noch keine Aufgabe beantwortet."
        : `${erledigt} beantwortet, ${richtig} richtig, ${falsch} falsch.`;
  return { striche, richtig, falsch, erledigt, gesamt, anteil, text };
}
